import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { memo, useEffect } from 'react';

import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { StopLossOrderCreationTile } from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/index.tsx';
import { useStopLossTileId } from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/stopLossContext.tsx';
import StopLossOrderEditingTile from '@/App/tiles/orders/StopLossOrder/StopLossOrderEditingTile/index.tsx';
import StopLossOrderExecutedTile from '@/App/tiles/orders/StopLossOrder/StopLossOrderExecutedTile/index.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { selectStopLossTile } from '@/store/orders/stopLoss/selectors.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export const StopLossOrderTile = memo(() => {
  const tileId = useStopLossTileId();
  const dispatch = useAppDispatch();
  const userPreferences = useAppSelector((state) => state.userPreferences);

  const { status, mode, errorCode } = useAppSelector((state) => {
    const tile = selectStopLossTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        status: 'creating',
      };
    }

    return {
      status: tile.properties.status.type,
      mode: tile.properties.mode,
      errorCode: tile.properties.status.type === 'failed' ? tile.properties.status.errorMessage : undefined,
    };
  }, deepEqual);

  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (status === 'pending') {
    return <ExecutionPendingOrderStatus isEditing={mode === 'EDIT'} />;
  }

  if (status === 'failed') {
    const handleReset = () => {
      dispatch(ordersSlice.actions.resetOrder({ tileId, userPreferences }));
    };

    return (
      <ErrorTile
        tileId={tileId}
        onReset={handleReset}
        errorCode={errorCode}
        quoteId={tileId}
        errorDescription={
          errorCode === 'order.timeout' ? 'order.timeout.error.message.description' : 'order.error.message.description'
        }
        errorTitle={errorCode === 'order.timeout' ? 'order.timeout.error.message.title' : 'order.error.message.title'}
      />
    );
  }

  if (mode === 'EDIT') {
    return <StopLossOrderEditingTile />;
  }

  if (status === 'creating') {
    return <StopLossOrderCreationTile />;
  }

  return <StopLossOrderExecutedTile />;
});
